import { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  IconButton,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  CircularProgress,
  Tooltip,
  Stack,
  Chip,
  InputAdornment,
} from '@mui/material';
import { Add, Edit, Block, Savings } from '@mui/icons-material';
import {
  useAportesExtra,
  useCreateAporteExtra,
  useUpdateAporteExtra,
  useDarDeBajaAporte,
  useConceptosAporte,
} from '../../../hooks/useAportesExtra';
import { formatDate, todayLocal } from '../../../utils/format';
import type { AporteExtraConConcepto } from '../../../types/afiliado';

interface Props {
  afiliadoId: string;
}

interface FormState {
  open: boolean;
  editing: AporteExtraConConcepto | null;
  concepto_aporte_id: string;
  porcentaje_override: string; // string para manejar input vacio
  fecha_desde: string;
  observaciones: string;
}

const todayISO = () => todayLocal();

const emptyForm: FormState = {
  open: false,
  editing: null,
  concepto_aporte_id: '',
  porcentaje_override: '',
  fecha_desde: todayISO(),
  observaciones: '',
};

export default function TabAportesExtra({ afiliadoId }: Props) {
  const { data: aportes = [], isLoading } = useAportesExtra(afiliadoId);
  const { data: conceptos = [] } = useConceptosAporte(true);
  const crear = useCreateAporteExtra();
  const editar = useUpdateAporteExtra();
  const dardeBaja = useDarDeBajaAporte();

  const [form, setForm] = useState<FormState>(emptyForm);

  const openCreate = () => setForm({ ...emptyForm, open: true });

  const openEdit = (a: AporteExtraConConcepto) =>
    setForm({
      open: true,
      editing: a,
      concepto_aporte_id: a.concepto_aporte_id,
      porcentaje_override: a.porcentaje_override?.toString() ?? '',
      fecha_desde: a.fecha_desde,
      observaciones: a.observaciones ?? '',
    });

  const close = () => setForm(emptyForm);

  const handleSubmit = async () => {
    const payload = {
      afiliado_id: afiliadoId,
      concepto_aporte_id: form.concepto_aporte_id,
      porcentaje_override: form.porcentaje_override
        ? Number(form.porcentaje_override)
        : null,
      fecha_desde: form.fecha_desde,
      fecha_hasta: null,
      observaciones: form.observaciones.trim() || null,
      asignado_por: null, // se completa server-side a futuro con audit
    };

    if (form.editing) {
      const { afiliado_id: _drop, ...updateOnly } = payload;
      void _drop;
      await editar.mutateAsync({ id: form.editing.id, payload: updateOnly });
    } else {
      await crear.mutateAsync(payload);
    }
    close();
  };

  const handleDarDeBaja = async (a: AporteExtraConConcepto) => {
    if (!confirm(`Dar de baja el aporte "${a.concepto.descripcion}"?`)) return;
    await dardeBaja.mutateAsync({ id: a.id, afiliado_id: afiliadoId });
  };

  const isSubmitting = crear.isPending || editar.isPending;
  const canSubmit = !!form.concepto_aporte_id && !!form.fecha_desde;

  // Aportes activos: sin fecha_hasta o fecha_hasta futura
  const today = todayISO();
  const isActivo = (a: AporteExtraConConcepto) =>
    !a.fecha_hasta || a.fecha_hasta > today;

  // Conceptos disponibles para asignar: los que NO tiene actualmente activos
  const conceptosActivos = new Set(
    aportes.filter(isActivo).map((a) => a.concepto_aporte_id)
  );
  const conceptosDisponibles = conceptos.filter(
    (c) => !conceptosActivos.has(c.id) || form.editing?.concepto_aporte_id === c.id
  );

  return (
    <Box>
      <Alert severity="info" sx={{ mb: 2 }}>
        <strong>Aportes adicionales del afiliado.</strong> Estos aportes se suman
        a los conceptos automaticos (Sindical 2% / Solidario 1%) en cada DDJJ.
        Los conceptos disponibles aqui son los marcados como <em>opcionales</em>{' '}
        en la tabla <strong>Conceptos de aporte</strong> (Configuracion).
      </Alert>

      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ mb: 2 }}
      >
        <Typography variant="subtitle1" fontWeight={600}>
          Aportes extra asignados ({aportes.length})
        </Typography>
        <Button
          variant="outlined"
          startIcon={<Add />}
          onClick={openCreate}
          size="small"
          disabled={conceptos.length === 0}
        >
          Asignar aporte
        </Button>
      </Stack>

      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : aportes.length === 0 ? (
        <Box
          sx={{
            textAlign: 'center',
            py: 6,
            border: '1px dashed',
            borderColor: 'divider',
            borderRadius: 1,
            color: 'text.secondary',
          }}
        >
          <Savings sx={{ fontSize: 40, mb: 1 }} />
          <Typography variant="body2">
            Este afiliado no tiene aportes extra asignados.
          </Typography>
          {conceptos.length === 0 && (
            <Typography variant="caption" display="block" sx={{ mt: 1 }}>
              No hay conceptos opcionales disponibles para asignar. Cargalos
              en Configuracion &gt; Conceptos de aporte.
            </Typography>
          )}
        </Box>
      ) : (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Concepto</TableCell>
              <TableCell>Porcentaje</TableCell>
              <TableCell>Vigencia</TableCell>
              <TableCell>Estado</TableCell>
              <TableCell>Observaciones</TableCell>
              <TableCell align="right">Acciones</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {aportes.map((a) => {
              const activo = isActivo(a);
              const porcEfectivo = a.porcentaje_override ?? a.concepto.porcentaje;
              const conOverride = a.porcentaje_override != null;
              return (
                <TableRow key={a.id} hover>
                  <TableCell>
                    <strong>{a.concepto.codigo}</strong>
                    <Typography variant="caption" display="block" color="text.secondary">
                      {a.concepto.descripcion}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography fontWeight={600}>
                      {porcEfectivo}%
                    </Typography>
                    {conOverride && (
                      <Typography variant="caption" color="warning.main">
                        (base: {a.concepto.porcentaje}%)
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      Desde: {formatDate(a.fecha_desde)}
                    </Typography>
                    {a.fecha_hasta && (
                      <Typography variant="caption" color="text.secondary">
                        Hasta: {formatDate(a.fecha_hasta)}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={activo ? 'Activo' : 'Dado de baja'}
                      size="small"
                      color={activo ? 'success' : 'default'}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ maxWidth: 200 }} noWrap>
                      {a.observaciones || '—'}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    {activo && (
                      <>
                        <Tooltip title="Editar">
                          <IconButton size="small" onClick={() => openEdit(a)}>
                            <Edit fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Dar de baja">
                          <IconButton
                            size="small"
                            color="warning"
                            onClick={() => handleDarDeBaja(a)}
                          >
                            <Block fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}

      <Dialog open={form.open} onClose={close} maxWidth="sm" fullWidth>
        <DialogTitle>
          {form.editing ? 'Editar aporte extra' : 'Asignar aporte extra'}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <TextField
              select
              required
              fullWidth
              label="Concepto de aporte"
              value={form.concepto_aporte_id}
              onChange={(e) =>
                setForm({ ...form, concepto_aporte_id: e.target.value })
              }
              disabled={!!form.editing}
              helperText={
                form.editing
                  ? 'No se puede cambiar el concepto: dar de baja y crear uno nuevo'
                  : 'Solo aparecen conceptos opcionales no asignados'
              }
            >
              {conceptosDisponibles.length === 0 ? (
                <MenuItem disabled value="">
                  No hay conceptos disponibles
                </MenuItem>
              ) : (
                conceptosDisponibles.map((c) => (
                  <MenuItem key={c.id} value={c.id}>
                    <Box>
                      <strong>{c.codigo}</strong> — {c.descripcion}
                      <Typography variant="caption" display="block" color="text.secondary">
                        Porcentaje base: {c.porcentaje}%
                      </Typography>
                    </Box>
                  </MenuItem>
                ))
              )}
            </TextField>

            <TextField
              fullWidth
              type="number"
              label="Porcentaje override (opcional)"
              value={form.porcentaje_override}
              onChange={(e) =>
                setForm({ ...form, porcentaje_override: e.target.value })
              }
              helperText="Si vacio, se aplica el porcentaje base del concepto"
              InputProps={{
                endAdornment: <InputAdornment position="end">%</InputAdornment>,
                inputProps: { min: 0, max: 100, step: 0.01 },
              }}
            />

            <TextField
              required
              fullWidth
              type="date"
              label="Vigente desde"
              value={form.fecha_desde}
              onChange={(e) => setForm({ ...form, fecha_desde: e.target.value })}
              InputLabelProps={{ shrink: true }}
            />

            <TextField
              fullWidth
              multiline
              rows={2}
              label="Observaciones"
              value={form.observaciones}
              onChange={(e) => setForm({ ...form, observaciones: e.target.value })}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={close} disabled={isSubmitting}>
            Cancelar
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={!canSubmit || isSubmitting}
            startIcon={isSubmitting ? <CircularProgress size={16} color="inherit" /> : null}
          >
            {form.editing ? 'Guardar cambios' : 'Asignar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
